import { Store } from 'redux';
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { State } from '../reducers/typings';
import api from '../services/api';
import * as authActions from '../actions/auth';
import * as authService from '../services/auth';

interface AsyncResponse {
  task_status: string;
  task_response: AxiosResponse;
}

const DEFAULT_ASYNC_POLLING_INTERVAL_MS = 1000;
const POLLING_TIMEOUT_MS = 50000;
const REQUEST_TIMEOUT_MS = 5000;

export function isNetworkError(err?: AxiosError): boolean {
  if (err?.code) {
    if (
      err.code === 'ECONNABORTED' ||
      err.code === 'ECONNREFUSED' ||
      err.code === 'ECONNRESET' ||
      err.code === 'EHOSTUNREACH' ||
      err.code === 'ENETUNREACH' ||
      err.code === 'ETIMEDOUT' ||
      err.code === 'ENOTFOUND' ||
      err.code === 'EAI_AGAIN'
    ) {
      return true;
    }
  }
  return false;
}

// eslint-disable-next-line import/prefer-default-export
export async function initApi(store: Store<State>): Promise<void> {
  api.interceptors.request.use(
    async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      if (config.url?.includes('auth')) return config;
      if (!authService.isLoggedIn()) {
        store.dispatch(authActions.logout());
        return Promise.reject();
      }
      return config;
    },
  );

  api.interceptors.response.use(
    async (response: AxiosResponse): Promise<AxiosResponse> => {
      const intervalHeader = +(response.headers['async-task-polling-interval'] ?? '');

      const pollingInterval = Number.isNaN(intervalHeader)
        ? DEFAULT_ASYNC_POLLING_INTERVAL_MS
        : intervalHeader;
      const asyncTaskUrlHeader = response.headers['async-task-url'] as string | undefined;

      if (
        response.status === 202 &&
        asyncTaskUrlHeader &&
        response.headers['async-task-polling-interval']
      ) {
        const responseHeaders = response.config.headers;

        const startTime = Date.now();

        let asyncTaskResponse: AxiosResponse<AsyncResponse> | undefined;

        do {
          // eslint-disable-next-line no-await-in-loop
          await new Promise<void>((resolve: () => void) => setTimeout(resolve, pollingInterval));
          try {
            // eslint-disable-next-line no-await-in-loop
            asyncTaskResponse = await api.get(asyncTaskUrlHeader, {
              headers: responseHeaders,
              timeout: REQUEST_TIMEOUT_MS,
            });
          } catch (err) {
            if (isAxiosError(err) && !isNetworkError(err)) {
              return Promise.reject(err);
            }
          }
        } while (
          (asyncTaskResponse === undefined || asyncTaskResponse.data.task_status === 'PENDING') &&
          Date.now() - startTime < POLLING_TIMEOUT_MS
        );

        switch (asyncTaskResponse?.data.task_status) {
          case 'PENDING': {
            return Promise.reject(new AxiosError('Task timed out'));
          }
          case 'RETRY':
          case 'FAILURE': {
            return Promise.reject(new AxiosError('Task failed'));
          }
          case 'SUCCESS': {
            if (
              asyncTaskResponse.data.task_response.status >= 200 &&
              asyncTaskResponse.data.task_response.status < 300
            ) {
              return Promise.resolve(asyncTaskResponse.data.task_response);
            }
            return Promise.reject(
              new AxiosError(
                'Task response error',
                undefined,
                undefined,
                undefined,
                asyncTaskResponse.data.task_response,
              ),
            );
          }
          default: {
            return Promise.reject(new AxiosError('Unknown task status'));
          }
        }
      }
      return response;
    },
  );
}
